<script setup lang="ts">
import { FaxSchema } from '~/schemas/fax';

const { data: page } = await useAsyncData(() => 
  queryCollection('FaxPage').first()
);

useSeoMeta({
  title: page.value?.metaTitle,
  description: page.value?.metaDescription,
  ogUrl: page.value?.linkCanonical,
  ogTitle: page.value?.title,
  ogDescription: page.value?.description,
});

useHead({
  link: [
    { rel: "canonical", href: page.value?.linkCanonical },
  ],
  script: [
    {
      type: 'application/ld+json',
      textContent: JSON.stringify(FaxSchema)
    },
  ],
});


const modalsStore = useModalsStore();

function handleOpenModal() {
  modalsStore.openModal('CTA');
};

function handleOnClickViewPrices() {
  const elem = document.getElementById('prices');
  elem?.scrollIntoView({ behavior: 'smooth' });
};
</script>

<template>
  <template v-if="page">
    <SectionsBase class="relative mb-10">
      <svg 
        class="absolute w-full h-[38%] bottom-50.75 left-0 bg-linear-to-bl from-primary-500 to-primary-800
          rounded-tr-4xl lg:rounded-tr-none rounded-br-[140px] lg:bottom-auto lg:top-0 lg:w-[45%] lg:h-[calc(100%-32px)]"
      />
      <div class="container flex flex-col lg:flex-row items-center relative z-1 lg:gap-5">
        <div class="w-full lg:bg-[url('/img/new/backgrounds/mesh-01.webp')] bg-bottom bg-no-repeat">
          <h1 class="text-primary-600 highlight">{{ page.title }}</h1>
          <span class="h1"><span class="semicolon">؛</span> {{ page.subTitle }}</span>
          <p class="text-gray-500 text-body1-semibold mt-6 max-w-200 text-center lg:text-start">{{ page.description }}</p>
          <div class="w-full flex flex-col lg:flex-row items-center my-10 lg:my-15 gap-5">
            <Button
              :append-icon="{ name: 'solar:round-alt-arrow-left-bold' }"
              size="Large"
              pill
              @click="handleOpenModal"
              class="w-46.5"
            >
              رایگان امتحان کنید
            </Button>
            <Button
              type="Outline"
              :append-icon="{ name: 'solar:round-alt-arrow-left-bold' }"
              size="Large"
              pill
              @click="handleOnClickViewPrices"
              class="w-46.5 [&_.btn-slot-parent]:w-full [&_.btn-slot-parent]:justify-between!"
            >
              مشاهده تعرفه‌ها
            </Button>
          </div>
          <div class="desktop-only w-full lg:flex! flex-col my-15 gap-3">
            <div 
              v-for="item in page.items"
              :key="item"
              class="flex items-start gap-3"
            >
              <Icon name="solar:check-circle-bold" size="24" class="text-secondary-600" />
              <span class="text-gray-400 text-body1-semibold">{{ item }}</span>
            </div>
          </div>
        </div>
        <NuxtImg
          width="700"
          height="577"
          :src="page.img"
          :alt="page.title"
          sizes="250px md:350px lg:600px 2xl:700px"
          :preload="{ fetchPriority: 'high' }"
          fetchpriority="high"
          loading="eager"
          class="mx-auto lg:w-175 mt-12"
        />
        <div 
          class="mobile-only w-full flex flex-col pb-10 mt-10 -mb-7.5 gap-3 bg-[url('/img/new/backgrounds/mesh-01.webp')]
            bg-bottom bg-no-repeat"
        >
          <div 
            v-for="item in page.items"
            :key="item"
            class="flex items-start gap-3"
          >
            <Icon name="solar:check-circle-bold" size="24" class="text-secondary-600" />
            <span class="text-gray-400 text-body1-semibold">{{ item }}</span>
          </div>
        </div>
      </div>
    </SectionsBase>

    <LazyIdleSectionsLogos hydrate-on-idle />

    <LazyVisibleTOC hydrate-on-visible :topics="page.topics" />

    <LazyVisibleSectionsFeaturesFax
      hydrate-on-visible
      :title="page.features.title"
      :desc="page.features.desc"
      :items="page.features.items"
    />

    <LazyVisibleSectionsLaunchSteps
      hydrate-on-visible
      :title="page.launchSteps.title"
      :desc="page.launchSteps.desc"
      :items="page.launchSteps.items"    
    />

    <LazyVisibleSectionsWhyNavatel
      hydrate-on-visible
      :title="page.whyNavatel.title"
      :desc="page.whyNavatel.desc"
      :items="page.whyNavatel.items"
    />

    <LazyVisibleSectionsNavatelOverview
      hydrate-on-visible
      :title="page.navatelOverview.title"
      :subTitle="page.navatelOverview.subTitle"
      :desc="page.navatelOverview.desc"
    />

    <LazyVisibleSectionsPricesFax
      hydrate-on-visible
      :title="page.prices.title"
      :desc="page.prices.desc"
    />

    <LazyVisibleSectionsComments hydrate-on-visible />

    <SectionsFAQ
      :title="page.FAQ.title"
      :items="page.FAQ.items"
    />
  </template>
</template>
